import { useEffect, useMemo } from 'react';
import {
  Documentation,
  FilterGroupingEnum,
  FormTemplateQuestionFieldOption,
} from 'graphql-common';
import { useEdmDocumentationsLightQuery } from '@lib/graphql-common';
import _get from 'lodash/get';

type Params = {
  fieldId: string;
  options: FormTemplateQuestionFieldOption[];
  registerFieldDocs?: (
    fieldId: string,
    docs: Record<string, Documentation>,
  ) => void;
};

export default function useDocumentationsByIds({
  fieldId,
  options,
  registerFieldDocs,
}: Params) {
  const documentationIds = useMemo(() => {
    const ids = new Set<string>();
    options.forEach((opt) => {
      const docId = opt.optionDocumentation?.documentationId;
      if (docId) ids.add(docId);
    });
    return Array.from(ids);
  }, [options]);

  const docsQuery = useEdmDocumentationsLightQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      limit: documentationIds.length || 1,
      filters: documentationIds.length
        ? [
            {
              id: {
                grouping: FilterGroupingEnum.And,
                predicate: { in: documentationIds },
              },
            },
          ]
        : undefined,
    },
    skip: !documentationIds.length,
  });

  const docsById = useMemo(() => {
    const map: Record<string, Documentation> = {};
    const collection = (_get(docsQuery, 'data.data.collection', []) ||
      []) as Documentation[];
    collection.forEach((doc) => {
      map[doc.id] = doc;
    });
    return map;
  }, [docsQuery]);

  useEffect(() => {
    if (!registerFieldDocs) return;
    if (docsQuery.loading && !docsQuery.data) return;
    registerFieldDocs(fieldId, docsById);
  }, [fieldId, docsById, docsQuery.loading, docsQuery.data, registerFieldDocs]);

  const isFirstLoading =
    documentationIds.length > 0 && docsQuery.loading && !docsQuery.data;

  return { docsById, isFirstLoading };
}
